"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Shield, ShieldAlert, ShieldCheck, Loader2, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ScanResult {
  score: number;
  summary: { critical: number; high: number; medium: number; low: number; info: number };
  topFindings: { severity: string; title: string; category: string }[];
  totalFindings: number;
  scannedUrl: string;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-800 dark:text-red-400",
  high: "bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/30 dark:border-orange-800 dark:text-orange-400",
  medium: "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950/30 dark:border-yellow-800 dark:text-yellow-400",
  low: "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-400",
  info: "bg-muted border-border text-muted-foreground",
};

function ScoreLabel(score: number) {
  if (score >= 80) return { text: "Good", color: "text-green-600 dark:text-green-400" };
  if (score >= 50) return { text: "Needs work", color: "text-yellow-600 dark:text-yellow-400" };
  return { text: "At risk", color: "text-red-600 dark:text-red-400" };
}

function ScoreRing({ score }: { score: number }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const fill = (score / 100) * circumference;
  const color = score >= 80 ? "#22c55e" : score >= 50 ? "#eab308" : "#ef4444";
  const { text, color: labelColor } = ScoreLabel(score);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-28 w-28">
        <svg className="h-28 w-28 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/40" />
          <circle
            cx="50" cy="50" r={radius} fill="none"
            stroke={color} strokeWidth="8"
            strokeDasharray={`${fill} ${circumference}`}
            strokeLinecap="round"
            className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold">{score}</span>
          <span className="text-[10px] text-muted-foreground">/100</span>
        </div>
      </div>
      <span className={cn("text-sm font-semibold", labelColor)}>{text}</span>
    </div>
  );
}

export function FreeScanWidget() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState("");

  const runScan = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;

    let normalized = trimmed;
    if (!normalized.startsWith("http")) normalized = `https://${normalized}`;

    try { new URL(normalized); } catch {
      setError("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setResult(null);
    setError("");

    try {
      const res = await fetch("/api/security/public-scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalized }),
      });
      if (res.status === 429) {
        setError("Too many scans. Please wait a minute and try again.");
        return;
      }
      if (!res.ok) {
        setError("Scan failed. Make sure the URL is publicly accessible.");
        return;
      }
      const data: ScanResult = await res.json();
      setResult(data);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Input row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="url"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && !loading && runScan()}
            placeholder="https://yourwebsite.com"
            className="w-full rounded-xl border border-border bg-background pl-10 pr-4 py-3 text-sm outline-none focus:border-foreground/30 transition-colors placeholder:text-muted-foreground"
            disabled={loading}
          />
        </div>
        <Button
          onClick={runScan}
          disabled={loading || !url.trim()}
          className="rounded-xl px-5 bg-foreground text-background hover:bg-foreground/90 shrink-0 gap-2"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
          {loading ? "Scanning..." : "Scan free"}
        </Button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      <p className="mt-2 text-xs text-muted-foreground text-center">
        Free passive scan — no signup, no install. Takes ~15 seconds.
      </p>

      {/* Loading state */}
      {loading && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 space-y-3">
          {["Checking SSL/TLS...", "Scanning for exposed secrets...", "Testing security headers...", "Checking CORS configuration..."].map((step, i) => (
            <div key={step} className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" style={{ animationDelay: `${i * 150}ms` }} />
              {step}
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
          {/* Score header */}
          <div className="p-6 border-b border-border flex items-center gap-6">
            <ScoreRing score={result.score} />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1 truncate">{result.scannedUrl}</p>
              <p className="text-lg font-semibold">
                {result.totalFindings === 0
                  ? "No issues detected"
                  : `${result.totalFindings} issue${result.totalFindings > 1 ? "s" : ""} found`}
              </p>
              {/* Severity pills */}
              <div className="flex flex-wrap gap-2 mt-3">
                {(["critical", "high", "medium", "low"] as const).map((s) => {
                  const count = result.summary[s];
                  if (count === 0) return null;
                  return (
                    <span key={s} className={cn("text-[11px] font-semibold px-2.5 py-1 rounded-full border", SEVERITY_STYLES[s])}>
                      {count} {s}
                    </span>
                  );
                })}
                {result.totalFindings === 0 && (
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-green-50 border-green-200 text-green-700 dark:bg-green-950/30 dark:border-green-800 dark:text-green-400">
                    All clear
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Top findings */}
          {result.topFindings.length > 0 && (
            <div className="divide-y divide-border/50">
              {result.topFindings.map((f, i) => (
                <div key={i} className="flex items-center gap-3 px-6 py-3">
                  <span className={cn("text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border shrink-0", SEVERITY_STYLES[f.severity])}>
                    {f.severity}
                  </span>
                  <span className="text-sm text-foreground/80 truncate">{f.title}</span>
                </div>
              ))}
              {result.totalFindings > 5 && (
                <div className="px-6 py-3 text-xs text-muted-foreground">
                  +{result.totalFindings - 5} more issues hidden — sign up for full report
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="p-5 bg-muted/30 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">
                {result.score < 80 ? "Fix these issues before attackers find them" : "Stay ahead of vulnerabilities"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Full scan + QA testing + reports — free to start
              </p>
            </div>
            <Button asChild size="sm" className="rounded-full px-5 bg-foreground text-background hover:bg-foreground/90 shrink-0 gap-1.5">
              <Link href="/register">
                Get full report <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
